import { CustomizationValue } from './CustomizationValue';
import {
  SLTXFormatRootFileVariant,
} from '../../common/libraries/fileFormats/sltx/sltx';

import { POSCatalogModelData } from '../../common/libraries/modelViewer/Data/POSCatalogData';
import { TreeOptionSelections, buildTreeOptionSelections } from '../../common/libraries/modelConfiguration/data/optionsSelection';
import { ModelGroupInfo } from '../../common/libraries/fileFormats/sltx/modelGroupInfo';
import { CustomAttributeValue } from '../../common/libraries/customAttributes/customAttributeValue';

export class CatalogObjectControllerData {

  constructor(
    catalogModelData : POSCatalogModelData,
    variant : SLTXFormatRootFileVariant | undefined,
  ) {

    this.catalogModelData = catalogModelData;

    if (variant) {
      this.variantComposition = buildTreeOptionSelections(
        variant.variantComposition,
      );
      this.variantBackgroundColor = variant.backgroundColor;
      this.lastVariantSelected = variant.name;
    } else {
      //
      // If we have no variant, but we have options, create a default variant
      // with the first option from each group

      // eslint-disable-next-line no-lonely-if
      if (catalogModelData.hasConfigurableGroups()) {
        this.variantComposition = [];

        const catalogObjectGroups = catalogModelData.getGroups();
        catalogObjectGroups.forEach((objectGroup) => {

          const groupOptions = ModelGroupInfo.getGroupAllOptions(objectGroup);
          if (groupOptions && groupOptions.length > 0) {

            this.variantComposition.push({
              groupID: objectGroup.id,
              optionID: groupOptions[0].id,
            });
          }
        });

        this.variantBackgroundColor = '#ffffff';
      }
    }
  }

  public getVariantComposition() {
    return this.variantComposition;
  }

  static CreateFromCatalogObjectControllerData(
    objectControllerData : CatalogObjectControllerData,
    variantComposition : TreeOptionSelections,
    lastVariantSelected : string,
  ) {
    const newObject = new CatalogObjectControllerData(
      objectControllerData.catalogModelData,
      undefined,
    );

    // Customizations
    newObject.customizationData = [...objectControllerData.customizationData];
    // Variant definition
    newObject.variantComposition = [...variantComposition];
    // Variant background color
    newObject.variantBackgroundColor = objectControllerData.variantBackgroundColor;
    // Custom attributes values
    newObject.customAttributesValues = [...objectControllerData.customAttributesValues];

    // Last variant selected
    if (lastVariantSelected.length > 0) {
      newObject.lastVariantSelected = lastVariantSelected;
    } else {
      newObject.lastVariantSelected = objectControllerData.lastVariantSelected;
    }

    return newObject;
  }

  public getSelectedOptionForComponent(componentId: number, leafOnly : boolean) : string {
    // We need to go to the selected options, grab the ID, and then grab the name
    // eslint-disable-next-line max-len
    const selectedVariantComponent = this.getVariantComposition().find(
      (v) => v.groupID === componentId,
    )!;

    if (!selectedVariantComponent || selectedVariantComponent.optionID === undefined) return '';

    const selectedVariantComponentOption = selectedVariantComponent.optionID;

    // Now, grab the option name
    const objectComponent = this.catalogModelData.getGroups().find(
      (g) => g.id === componentId,
    )!;

    let objectComponentOptionName = '';
    objectComponent.optionsFolders.forEach((of) => {
      const folderName = of.name;
      of.options.forEach((o) => {
        if (o.id === selectedVariantComponentOption) {
          objectComponentOptionName = leafOnly ? `${o.name}` : `${folderName}+${o.name}`;
        }
      });
    });
    return objectComponentOptionName;
  }

  public hasOptionWithName(optionName: string) : boolean {
    const group = this.catalogModelData.getGroups().find(
      (g) => this.getSelectedOptionForComponent(g.id, true) === optionName,
    );
    return group !== undefined;
  }

  public CreateCopy()
  : CatalogObjectControllerData {

    const newObject = new CatalogObjectControllerData(
      this.catalogModelData,
      undefined,
    );

    // Customizations
    newObject.customizationData = [...this.customizationData];
    // Variant definition
    newObject.variantComposition = [...this.variantComposition];
    // Variant background color
    newObject.variantBackgroundColor = this.variantBackgroundColor;

    return newObject;
  }

  public catalogModelData : POSCatalogModelData;
  public variantComposition : TreeOptionSelections = [];
  public customAttributesValues: CustomAttributeValue[] = [];
  public customizationData : CustomizationValue[] = [];
  public variantBackgroundColor : string = '';
  public lastVariantSelected : string = '';
}
